import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, concatMap, tap } from 'rxjs/operators';

import { BaseSortableSearchComponent } from '@components/_base/base-search/base-sortable-search.component';
import { YesNoMessageboxComponent } from '@components/_base/_messageboxes/yes-no-messagebox/yes-no-messagebox.component';
import { modalMinWidth } from '@env/config';
import { displayError } from '@app/_utils/error-util';
import { UIEventCustom } from '@app/_utils/ui-event-util';
import { PermissionsService } from '@app/login/_services/permissions.service';

import { Blacklist, BlacklistReason } from '../_models/blacklist.model';
import { BlacklistService } from '../_services/blacklist.service';

@Component({
  selector: 'app-list-blacklists',
  templateUrl: './list-blacklists.component.html',
  styleUrls: ['./list-blacklists.component.css']
})
export class ListBlacklistsComponent extends BaseSortableSearchComponent<Blacklist> implements OnInit, OnDestroy {

  searchForm = this.fb.group({
    q:             [null],
    channelFilter: [null],
    reason:        [null]
  });

  searchSubscription: Subscription;

  search$ = this.searchSubject.asObservable().pipe(
    tap(() => this.dataLoaded = false),
    concatMap(() =>
      this.blacklistService.pageable(this.sortingPaging, this.searchForm.value).pipe(
        tap(([page, totalCount]) => {
          this.sortingPaging.fromRow = page.fromRow;
          this.sortingPaging.toRow = page.toRow;
          this.sortingPaging.totalElements = totalCount;
          this.content = page.content;
          this.dataLoaded = true;
        })
      )
    ),
    catchError(err => {
      displayError(err);
      this.dataLoaded = false;
      this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      return EMPTY;
    })
  );

  constructor(
    private fb:               FormBuilder,
    private router:           Router,
    private dialog:           MatDialog,
    private uiEvent:          UIEventCustom,
    private translateService: TranslateService,
    private blacklistService: BlacklistService,
    public  perms:            PermissionsService,
  ) {
    super();
  }

  ngOnInit() {
    this.searchSubscription = this.search$.subscribe();
    this.searchSubject.next();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
  }

  toDate(value: any): Date | null {
    if (!value) return null;
    if (Array.isArray(value)) {
      const [y, m, d, h = 0, min = 0, s = 0] = value;
      return new Date(y, m - 1, d, h, min, s);
    }
    return new Date(value);
  }

  channelLabel(entry: Blacklist): string {
    if (entry.smsChannel && entry.emailChannel) return 'SMS, E-mail';
    if (entry.smsChannel) return 'SMS';
    if (entry.emailChannel) return 'E-mail';
    return '';
  }

  reasonKey(entry: Blacklist): string {
    const map: { [k in BlacklistReason]: string } = {
      'gdpr-request':   'blacklist.reason.gdpr',
      'unsubscribe':    'blacklist.reason.unsubscribe',
      'spam-complaint': 'blacklist.reason.spam',
      'other':          'blacklist.reason.other'
    };
    return entry.reason ? map[entry.reason] : '';
  }

  delete(id: number) {
    const dialogRef = this.dialog.open(YesNoMessageboxComponent, {
      ...modalMinWidth,
      data: { isWarning: true, message: 'blacklist.deleteConfirm' }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.blacklistService.delete(id).subscribe({
        next: () => {
          this.uiEvent.displayUISuccess();
          this.searchSubject.next();
        },
        error: err => {
          displayError(err);
          this.uiEvent.displayUIError();
        }
      });
    });
  }
}
